import React, { useState } from "react";

import { useGetCurrentCompanyApplicationQuery } from "EmployerApplication/api";
import { ApplicationLayout } from "EmployerApplication/Components";
import EditEmployeeEmailModal from "EmployerApplication/Components/EditEmployeeEmailModal";
import { addEmployeesURL, selectContributionTiersURL, documentsUploadPageURL, api } from "EmployerApplication/urls";
import GenericEmail from "images/employer_application/review_employee_email_sample.png";
import SquareEmail from "images/employer_application/review_employee_email_sample_square.png";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import isSquare from "Square/isSquare";
import companyToken from "Utils/companyToken";
import { compareByEmail } from "Utils/comparisons";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { isNonPartner } from "Utils/isPartner";
import { BasicLoading } from "ZeroRuby";

import { BodyMedium } from "Cobalt/BodyText";
import { Card } from "Cobalt/Card";
import { MenuDropdown, Option } from "Cobalt/Dropdown";
import { StandaloneTrigger } from "Cobalt/Modal";
import { Table, HeaderTR, TH, TR, TD, EllipsisTD, SnugTD } from "Cobalt/TableV2";
import AgentHeader from "Common/Agent/AgentHeader";
import Icon from "Common/Icon";
import NavigationControls from "Common/NavigationControls";
import { Surface, SurfaceSection, SurfaceHeader } from "Theme/Surface";

const getEmailImage = () => {
  if (isSquare()) {
    return SquareEmail;
  }
  return GenericEmail;
};

export const ReviewEmployeeEmailsTable = ({ employees }) => (
  <Table>
    <thead>
      <HeaderTR>
        <TH width="200px">Employee</TH>
        <TH>Email</TH>
        <TH />
      </HeaderTR>
    </thead>
    <tbody>
      {Object.values(employees)
        .sort(compareByEmail)
        .map(
          (employee) =>
            employee.is_eligible && (
              <TR highlightFirstCell={false} key={employee.hash_id}>
                <EllipsisTD>{employee.full_name}</EllipsisTD>
                <TD>{employee.email ? employee.email : "--"}</TD>
                <SnugTD>
                  {isNonPartner() && (
                    <MenuDropdown pullRight>
                      <StandaloneTrigger
                        employee={employee}
                        trigger={(props) => (
                          <Option
                            onClick={(e) => {
                              props.onClick(e);
                            }}
                          >
                            <Icon icon="pencil-alt" flavor="solid" mr={3} />
                            Edit
                          </Option>
                        )}
                        modal={(props) => <EditEmployeeEmailModal form={`editEmployeeEmailForm_${props.employee.hash_id}`} {...props} />}
                      />
                    </MenuDropdown>
                  )}
                </SnugTD>
              </TR>
            ),
        )}
    </tbody>
  </Table>
);

export const ReviewEmployeeEmailsPage = ({ employees, history }) => {
  const [submitting, setSubmitting] = useState(false);
  const { data: companyApplication, isLoading } = useGetCurrentCompanyApplicationQuery();

  const sendApplications = () => {
    setSubmitting(true);
    return fetchWithCSRFToken(api.sendEmployeeApplications, {
      method: "POST",
      body: JSON.stringify({ company_id_token: companyToken() }),
    })
      .then(() => {
        history.push(documentsUploadPageURL);
      })
      .catch((e) => {
        setSubmitting(false);
        throw e;
      });
  };

  if (isLoading) return <BasicLoading />;

  return (
    <Surface>
      <SurfaceHeader>Manage Employees</SurfaceHeader>
      <SurfaceSection>
        <AgentHeader>
          Now, let's email the applications to your employees. When they click Enroll or Decline, we'll show them the plan benefits and monthly premiums, and
          they will provide some basic personal information. This takes 5 minutes or less, typically.
        </AgentHeader>
      </SurfaceSection>
      <SurfaceSection title="Employee Application Email Preview">
        <>
          <BodyMedium>Your employees with email addresses entered will receive the following email:</BodyMedium>
          <Card p={0} my={5} style={{ maxWidth: "450px" }}>
            <img src={getEmailImage()} style={{ width: "100%" }} />
          </Card>
        </>
      </SurfaceSection>
      <SurfaceSection title="Eligible Employees">
        <BodyMedium mb={3}>For employees without email addresses, you will have the option to complete the application on their behalf later.</BodyMedium>
        <ReviewEmployeeEmailsTable employees={employees} />
      </SurfaceSection>
      <NavigationControls
        onClickSave={sendApplications}
        backLink={companyApplication?.hasTieredContributions ? selectContributionTiersURL : addEmployeesURL}
        submitting={submitting}
      />
    </Surface>
  );
};

const withRedux = connect((state) => ({
  employees: state.employees,
}));

export default withRouter(withRedux(ReviewEmployeeEmailsPage));
